import {AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {DOCUMENT} from "@angular/common";
import {BehaviorSubject, Subject, Subscription, timer} from "rxjs";
import {filter, finalize, takeUntil} from "rxjs/operators";
import scrollIntoView from 'scroll-into-view-if-needed';
import {SimpleWord} from "../shared/models/simple-word.model";
import {SimpleDialect} from "../shared/models/dialect.model";
import {Proverb} from "../shared/models/proverb.model";
import {Video} from "../shared/models/video.model";
import {Saseung3} from "../shared/models/saseung3.model";
import {Example} from '../shared/models/example.model';
import {ExampleSJ} from '../shared/models/example-sj.model';
import {JASO, POOMSA, POOMSA_DISP_LISP} from "../shared/defines/gukeo.const";
import {GetSearchFromQuery} from '../shared/defines/client.func';
import {HISTORY_KEY_SEARCH_TOTAL, LIST_TYPE, SearchParam, UI_RELOAD_HEIGHT} from "../shared/defines/client.const";
import {
  BODY_HEIGHT_CAL,
  BODY_HEIGHT_VALUE,
  EXT_HEIGHT_CAL,
  LIST_SCROLL_INTERVAL,
  MENU_PAGE, SIDE_HEIGHT_DELTA,
  WORD_SCROLL_SPEED
} from "../shared/defines/desktop.const";
import {VoiceService} from "../shared/services/voice/voice.service";
import {TotalSearch, TotalService} from "../shared/services/total/total.service";
import {LayoutMgrService} from "../shared/services/layout-mgr/layout-mgr.service";
import {GlobalEventService} from "../shared/services/global-event/global-event.service";
import {ExampleBookmarkService} from '../shared/services/example/example-bookmark.service';
import {OptionService} from '../shared/services/option/option.service';
import {SearchBoxComponent} from "../shared/components/search-box/search-box.component";
import {WordDetailComponent} from "../shared/components/word-detail/word-detail.component";


@Component({
  selector: 'app-total',
  templateUrl: './total.component.html',
  styleUrls: ['./total.component.scss']
})
export class TotalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('main', { static: true }) main?: ElementRef; // ngOnInit() 에서 사용 가능
  @ViewChild('container') container?: ElementRef; // ngAfterViewInit() 에서 사용 가능
  @ViewChild('innerScroll') innerScroll?: ElementRef; // ngAfterViewInit() 에서 사용 가능

  // 화면 사이즈 맞춤에 사용
  @ViewChild('extContainer') extContainer?: ElementRef;
  @ViewChild(SearchBoxComponent) extHeader?: SearchBoxComponent;
  @ViewChild('extBody') extBody?: ElementRef;
  @ViewChild('extBody2') extBody2?: ElementRef;
  @ViewChild('wordDetail', { static: false }) wordDetail?: WordDetailComponent;

  protected readonly LIST_TYPE = LIST_TYPE;

  public selectedMenu: MENU_PAGE = MENU_PAGE.NONE;

  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam | null = null;

  // 라우터 처리 코드가 불려졌는지 여부
  private inited = false;

  // 검색창에 넣는 값
  public keyword: string | null = null;
  public placeholder: string | null = '이 곳의 내용, 무엇이든 검색!';
  protected scrollTop = true;
  protected readonly HISTORY_KEY_SEARCH_TOTAL = HISTORY_KEY_SEARCH_TOTAL;

  // 소리 자동 재생 관련
  protected autoPlay = false; // 화면에 처음 들어왔을 때, 자동 재생을 막는 변수.
  private lazyAutoPlay = false;

  // 로딩 중 처리
  public get IsLoading(): boolean {
    return this.totalSearch === null && this.totalSearch$ !== null;
  }
  private totalSearch$: Subscription | null = null;

  // 로딩 완료 시마다 불려짐
  protected loadComplete$ = new BehaviorSubject<boolean>(false);

  public totalSearch: TotalSearch | null = null;

  get countWord(): number {
    return this.totalSearch?.countWord ?? 0;
  }
  get listWord(): Array<SimpleWord> | null {
    return this.totalSearch?.listWord ?? null;
  }
  get lastWord(): SimpleWord | null {
    const listWord = this.totalSearch?.listWord ?? null;
    return (listWord && listWord.length > 0) ? listWord[listWord.length - 1] : null;
  }

  get countExample(): number {
    return this.totalSearch?.countExample ?? 0;
  }
  get listExample(): Array<Example> | null {
    return this.totalSearch?.listExample ?? null;
  }
  get lastExample(): Example | null {
    const listExample = this.totalSearch?.listExample ?? null;
    return (listExample && listExample.length > 0) ? listExample[listExample.length - 1] : null;
  }

  get countDialect(): number {
    return this.totalSearch?.countDialect ?? 0;
  }
  get listDialect(): Array<SimpleDialect> | null {
    return this.totalSearch?.listDialect ?? null;
  }
  get lastDialect(): SimpleDialect | null {
    const listDialect = this.totalSearch?.listDialect ?? null;
    return (listDialect && listDialect.length > 0) ? listDialect[listDialect.length - 1] : null;
  }

  get countProverb(): number {
    return this.totalSearch?.countProverb ?? 0;
  }
  get listProverb(): Array<Proverb> | null {
    return this.totalSearch?.listProverb ?? null
  }
  get lastProverb(): Proverb | null {
    const listProverb = this.totalSearch?.listProverb ?? null;
    return (listProverb && listProverb.length > 0) ? listProverb[listProverb.length - 1] : null;
  }

  get countVideo(): number {
    return this.totalSearch?.countVideo ?? 0;
  }
  get listVideo(): Array<Video> | null {
    return this.totalSearch?.listVideo ?? null;
  }
  get lastVideo(): Video | null {
    const listVideo = this.totalSearch?.listVideo ?? null;
    return (listVideo && listVideo.length > 0) ? listVideo[listVideo.length - 1] : null;
  }

  get countSaseung(): number {
    return this.totalSearch?.countSaseung ?? 0;
  }
  get listSaseung(): Array<Saseung3> | null {
    return this.totalSearch?.listSaseung ?? null;
  }
  get lastSaseung(): Saseung3 | null {
    const listSaseung = this.totalSearch?.listSaseung ?? null;
    return (listSaseung && listSaseung.length > 0) ? listSaseung[listSaseung.length - 1] : null;
  }


  curDetail: SimpleWord | Example | SimpleDialect | Proverb | Saseung3 | null = null;
  public get curWord(): SimpleWord | null {
    return this.curDetail instanceof SimpleWord ? this.curDetail : null;
  }
  public get curExample(): Example | null {
    return this.curDetail instanceof Example ? this.curDetail : null;
  }
  public get curExampleSJ(): ExampleSJ | null {
    return this.curExample?.toExampleSJ() ?? null;
  }
  public get curDialect(): SimpleDialect | null {
    return this.curDetail instanceof SimpleDialect ? this.curDetail : null;
  }
  public get curProverb(): Proverb | null {
    return this.curDetail instanceof Proverb ? this.curDetail : null;
  }
  public get curSaseung(): Saseung3 | null {
    return this.curDetail instanceof Saseung3 ? this.curDetail : null;
  }


  // 라우딩 처리
  private routerStop$ = new Subject();

  private queryParams$?: Subscription;
  private route$?: Subscription;
  private playExample$: Subscription | null = null;

  // 화면 크기 조정
  private bodyHeight = UI_RELOAD_HEIGHT;

  // 스크롤 처리
  private scrollDownInterval: any = null;

  protected showScrollDown = false;
  protected sendingExampleBookmark = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private route: ActivatedRoute,
    private voice: VoiceService,
    private option: OptionService,
    private totalSvc: TotalService,
    private exampleBmSvc: ExampleBookmarkService,
    private globalEventSvc: GlobalEventService,
    public lm: LayoutMgrService,
  ) {
    // super(globalEventSvc);

    /******************************************************************************************
     * 라우터 파리미터 변경 핸들러 - 주소 뒤의 파라미터를 받아오는 곳. ( ?keyword=... )
     * 입력된 파라미터가 없어도 처음 초기화 할 때에도 불려진다.
     * 처음 화면에 들어올 때, 라우터 주소 변견 핸들러보다 더 먼저 불려진다. (이상하지만 그렇다)
     *
     * router.navigate()로 같은 주소를 호출하면 이 코드가 다시 불려진다.
     */
    this.queryParams$ = this.route.queryParams
      .subscribe(params => {
        console.log('TotalComponent::queryParams =', this.inited, params, this.queryParams);

        const oldParams = this.queryParams;
        this.queryParams = {...params};

        if (this.inited) {
          // 라우터 주소 변경 핸들러로 들어와서 init()이 처음 불려진 경우 = 이미 화면이 목록이 불려진 경우 = 목록이 초기화되어있는 경우!
          // 이 조건을 안 걸면 init()가 두번 불려진다.

          if (params['keyword'] && params['keyword'] !== oldParams?.keyword) {
            // 다시 서버에서 받아온다.
            console.warn('TotalComponent::this.route.queryParams = keyword changed. reload list.');

            // id 지정된 것이 있다면 지운다
            delete this.queryParams['wid'];
            delete this.queryParams['eid'];
            delete this.queryParams['did'];
            delete this.queryParams['pid'];
            delete this.queryParams['sid'];

            // 열려있는 상세 창이 있다면 닫는다.
            this.curDetail = null;

            this.init();
          }

          this.openDetail();
        }
      });

    /******************************************************************************************
     * 라우터 주소 변경 핸들러 - 라우터 주소 읽어오는 곳.
     * 라우터 주소에 포함된 파라미터가 변경되어도 불려진다. (여기서는 type만 변경되어도 불려짐)
     *
     * queryParams가 바뀐 것(주소뒤의 파리미터가 변경된 경우)으로는 여기가 불려지지 않는다.
     */
    this.route$ = route.params.subscribe(params => {
      console.log('TotalComponent::params =', params, this.queryParams);
      this.inited = true;

      this.init();
    });


    /**
     * 로딩이 완료될 때마다 불려짐
     */
    this.loadComplete$.pipe(
      filter(success => success),
    ).subscribe(() => this.openDetail());
  }

  private openDetail(): void {
    console.warn('TotalComponent::openDetail() = ', this.lazyAutoPlay, this.autoPlay);

    if (this.queryParams?.wid) {
      this.curDetail = this.listWord?.find(w => w.id === this.queryParams?.wid) ?? null;
    }
    else if (this.queryParams?.eid) {
      this.curDetail = this.listExample?.find(e => e.id === this.queryParams?.eid) ?? null;
    }
    else if (this.queryParams?.did) {
      this.curDetail = this.listDialect?.find(d => d.id === this.queryParams?.did) ?? null;
    }
    else if (this.queryParams?.pid) {
      this.curDetail = this.listProverb?.find(p => p.id === this.queryParams?.pid) ?? null;
    }
    else if (this.queryParams?.sid) {
      this.curDetail = this.listSaseung?.find(s => s.id === this.queryParams?.sid) ?? null;
    }
    else {
      this.curDetail = null;
    }

    this.scrollToSelected();

    /**
     * 화면에 처음 들어올 때는 자동 재생을 막고, 그 다음부터 자동 재생 되도록 한다.
     * 화면에 처음 들어올 때, 무조건 첫번째 항목을 상세 화면에 표시하도록 되어있다. 이때는 소리 안 나야 함.
     */
    if (!this.lazyAutoPlay) {
      this.lazyAutoPlay = true;
    }
    else {
      this.autoPlay = true;
    }
  }

  private scrollToSelected(): void {
    timer(100).pipe(
      takeUntil(this.routerStop$),
    ).subscribe(() => {
      const selectedElementId = this.getSelectedElementId();
      if (!selectedElementId) {
        return;
      }

      const el = this.document.getElementById(selectedElementId);
      if (!el) {
        console.warn('TotalComponent::scrollToSelected() item not found', selectedElementId, this.queryParams);
        return;
      }

      scrollIntoView(el, {
        behavior: 'smooth',
        block: 'nearest',
        scrollMode: 'if-needed',
      });
    });
  }

  private getSelectedElementId(): string | null {
    if (this.queryParams?.wid) {
      return `total-word-${this.queryParams.wid}`;
    }
    if (this.queryParams?.eid) {
      return `total-example-${this.queryParams.eid}`;
    }
    if (this.queryParams?.did) {
      return `total-dialect-${this.queryParams.did}`;
    }
    if (this.queryParams?.pid) {
      return `total-proverb-${this.queryParams.pid}`;
    }
    if (this.queryParams?.sid) {
      return `total-saseung-${this.queryParams.sid}`;
    }
    return null;
  }

  ngOnInit(): void {

    // 페이지를 이동하면 소리를 멈추도록 구현
    // TODO: 이 부분은 좀 더 정밀하게 할 필요가 있다. 내 컴포넌트(TotalComponent)인 경우에만 처리하도록 수정할 필요가 있다.
    this.router.events.pipe(
      takeUntil(this.routerStop$),
      filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
    ).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.voice.Stop();
      }
      // else if (event instanceof NavigationEnd) {
      //
      // }
    });

    // if (this.main) {
    //   this.InitFocusMain(this.main);
    // }
    // else {
    //   console.error('TotalComponent::ngOnInit() main is null');
    // }
  }

  ngAfterViewInit(): void {
    this.updateExtHeight();

    // 최초 로딩 시, 화면 배치가 어그러지는 것을 고치기위한 코드. (TODO: 효과 있는지 확인 후 필요시 제거)
    timer(2000).subscribe(() => {
      this.updateExtHeight();
    });
  }

  ngOnDestroy(): void {
    // if (this.getNextList$) {
    //   this.getNextList$.unsubscribe();
    //   this.getNextList$ = null;
    // }

    // YouTube 플레이어 팝업이 열려있으면 닫기
    this.lm.CloseYoutubePopup();

    // 나가기 전에 창 닫기처리.
    // this.CloseDetail();

    // router 이동 체크를 중지함.
    this.routerStop$.next(null);

    // this.ExitFocusMain();

    this.queryParams$?.unsubscribe();
    this.route$?.unsubscribe();

    // totalSearch$ 구독 해지
    this.totalSearch$?.unsubscribe();

    // playExample$ 구독 해지
    this.playExample$?.unsubscribe();

    // scrollDownInterval이 남아있을 경우 클리어
    if (this.scrollDownInterval) {
      clearInterval(this.scrollDownInterval);
      this.scrollDownInterval = null;
    }
  }


  /**
   * 저장된 searchParam(라우팅에서 전달받은 옵션)을 이용해서 페이지를 재 초기화하고 다시 로딩한다.
   */
  private init(): void {
    let param: SearchParam;

    // id 파라미터가 있는 경우 이력화면으로 다시 이동해서 표시한다.
    // if (this.queryParams?.id) {
    //   this.historySvc.addHistory(this.queryParams.id);
    //   this.router.navigate(['/word', LIST_TYPE.HISTORY], {
    //     queryParams: this.queryParams,
    //   }).then();
    //   return;
    // }

    let jasoIdx: JASO | null;
    jasoIdx = null;
    [param, jasoIdx, this.keyword] = GetSearchFromQuery(this.queryParams);
    console.log(`keyword=${this.keyword}, param=${JSON.stringify(param)}`, this);
    this.ReloadList(param);
  }

  onSearch(param: SearchParam) {
    console.log('TotalComponent::onSearch() = ', param);

    if ('keyword' in param) {
      this.router.navigate(['/total'], {
        queryParams: {...param}
      }).then();
    }
  }

  private ReloadList(param: SearchParam) {
    console.warn('TotalComponent::ReloadList() = ', param);

    this.showScrollDown = false;

    if (param.keyword) {
      this.totalSearch$ = this.totalSvc.GetTotalSearch(param.keyword).pipe(
        finalize(() => {
          this.totalSearch$ = null;
        })
      ).subscribe(
        data => {
          console.log(data);
          this.totalSearch = data;
          this.loadComplete$.next(true);
        },
        (err: HttpErrorResponse) => {
          if (err.status === 404) {
            this.totalSearch = null;
            this.loadComplete$.next(false);
          }
          else {
            // TODO: 에러 출력
            console.error(err);
          }
        }
      );

      this.autoPlay = false;
      this.lazyAutoPlay = false;
    }
    else {
      console.error('TotalComponent::ReloadList() param does not contain keyword', param);
    }
  }


  onSelectWord($event: MouseEvent, word: SimpleWord): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    console.log('TotalComponent::onSelectWord', word);

    const queryParams = {
      ...this.queryParams,
      wid: word.id
    };
    delete queryParams['eid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['did'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['pid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['sid'];  // id는 한번에 하나만 지정해야 함.
    this.router.navigate(['/total'], {queryParams}).then();

    // 이미 로딩된 단어는 바로 소리를 재생한다.
    if (this.curDetail === word
      && this.voice.wordId !== word?.id  // 소리가 재생되고 있지 않을 때에만 재생한다.
    ) {
      this.wordDetail?.PlayWord();
    }
  }

  onSelectExample($event: MouseEvent, example: Example, forcePlay = false): void {
    $event.preventDefault();
    $event.stopPropagation();
    // [재생] 버튼 경로(forcePlay=true)에서는 이미 ExamplePlayBtnComponent.playSound가
    // globalEventSvc.Click을 emit했으므로 중복 emit을 방지한다.
    if (!forcePlay) {
      this.globalEventSvc.Click($event, true);
    }

    console.log('TotalComponent::onSelectExample', example, {forcePlay});

    const isReselection = this.curDetail === example;
    // 재생 중인 선택 항목은 라우팅으로 소리가 끊기거나 다시 시작되지 않도록 유지한다.
    if (!forcePlay && isReselection && this.voice.exampleId === example.id) {
      return;
    }

    const queryParams = {
      ...this.queryParams,
      eid: example.id
    };
    delete queryParams['wid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['did'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['pid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['sid'];  // id는 한번에 하나만 지정해야 함.
    // NavigationStart → voice.Stop() 이후에 재생이 살아남도록 navigate().then()에서 재생한다.
    this.router.navigate(['/total'], {queryParams}).then(navigated => {
      // 더 최신 탐색으로 취소된 항목의 음성이 뒤늦게 재생되지 않도록 한다.
      if (navigated === false) {
        return;
      }
      // 단어와 동일하게, 재생이 끝난 선택 항목을 다시 누르면 설정과 무관하게 재생한다.
      if (forcePlay || isReselection || this.option.autoPlay) {
        this.playExampleForce(example);
      }
    });
  }

  private playExampleForce(example: Example): void {
    if (!example.id || !example.exampleVoice) {
      console.error('TotalComponent::playExampleForce() missing example voice', example);
      return;
    }
    this.playExample$?.unsubscribe();
    this.playExample$ = this.voice.PlayExample(example.id, example.exampleVoice, 1).pipe(
      finalize(() => this.playExample$ = null),
    ).subscribe({
      error: err => console.error('TotalComponent::playExampleForce error', err),
    });
  }

  onSelectDialect($event: MouseEvent, dialect: SimpleDialect): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    console.log('TotalComponent::onSelectDialect', dialect);

    const queryParams = {
      ...this.queryParams,
      did: dialect.id
    };
    delete queryParams['wid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['eid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['pid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['sid'];  // id는 한번에 하나만 지정해야 함.
    this.router.navigate(['/total'], {queryParams}).then();
  }

  onSelectProverb($event: MouseEvent, proverb: Proverb): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    console.log('TotalComponent::onSelectProverb', proverb);

    const queryParams = {
      ...this.queryParams,
      pid: proverb.id
    };
    delete queryParams['wid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['eid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['did'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['sid'];  // id는 한번에 하나만 지정해야 함.
    this.router.navigate(['/total'], {queryParams}).then();
  }

  onSelectSaseung($event: MouseEvent, saseung: Saseung3): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    // Saseung3.id 는 string|null 이므로 null 가드 필요. 다른 모델(SimpleWord/Example/SimpleDialect/Proverb)의 id 는 string 이라 가드 불필요.
    if (!saseung.id) {
      console.error('TotalComponent::onSelectSaseung() invalid saseung id', saseung);
      return;
    }

    console.log('TotalComponent::onSelectSaseung', saseung);

    const queryParams = {
      ...this.queryParams,
      sid: saseung.id
    };
    delete queryParams['wid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['eid'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['did'];  // id는 한번에 하나만 지정해야 함.
    delete queryParams['pid'];  // id는 한번에 하나만 지정해야 함.
    this.router.navigate(['/total'], {queryParams}).then();
  }

  onToggleExampleBookmark($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();

    const example = this.curExample;
    if (!example?.id || this.sendingExampleBookmark) {
      return;
    }

    this.sendingExampleBookmark = true;
    const desired = !example.bookmark;
    example.bookmark = desired;
    this.exampleBmSvc.sendBookmark(example.id, desired).pipe(
      finalize(() => this.sendingExampleBookmark = false)
    ).subscribe({
      error: err => {
        console.error('TotalComponent::onToggleExampleBookmark() failed', example.id, desired, err);
        example.bookmark = !desired;
      },
    });
  }


  public GetPoomsaTxt(poomsa: POOMSA): string {
    return POOMSA_DISP_LISP[poomsa];
  }


  onWordViewInit(word: SimpleWord) {
    if (word.id === this.lastWord?.id) {
      timer(0).subscribe(() => {
        this.showScrollDown = this.innerScroll?.nativeElement.offsetHeight > this.container?.nativeElement.offsetHeight;
      });
    }
  }

  onDialectViewInit(dialect: SimpleDialect) {
    if (dialect.id === this.lastDialect?.id) {
      timer(0).subscribe(() => {
        this.showScrollDown = this.innerScroll?.nativeElement.offsetHeight > this.container?.nativeElement.offsetHeight;
      });
    }
  }

  onProverbViewInit(proverb: Proverb) {
    if (proverb.id === this.lastProverb?.id) {
      timer(0).subscribe(() => {
        this.showScrollDown = this.innerScroll?.nativeElement.offsetHeight > this.container?.nativeElement.offsetHeight;
      });
    }
  }

  onVideoViewInit(video: Video) {
    if (video.no === this.lastVideo?.no) {
      timer(0).subscribe(() => {
        this.showScrollDown = this.innerScroll?.nativeElement.offsetHeight > this.container?.nativeElement.offsetHeight;
      });
    }
  }

  onSaseungViewInit(saseung: Saseung3) {
    if (saseung.hanja === this.lastSaseung?.hanja) {
      timer(0).subscribe(() => {
        this.showScrollDown = this.innerScroll?.nativeElement.offsetHeight > this.container?.nativeElement.offsetHeight;
      });
    }
  }

  /****************************************************************************
   * 스크롤 처리
   ****************************************************************************/

  protected onScrollTop($event: Event): void {
    const target = $event.target as HTMLElement;

    // console.log('onScrollTop() ', target.scrollTop);
    this.scrollTop = target.scrollTop === 0; // 수직 스크롤 위치
  }


  onClickRope($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    this.container?.nativeElement.scrollTo({
      top: 0,
      behavior: 'smooth',
      // behavior: 'auto',
    });
  }

  onStartScrollDown($event: MouseEvent|TouchEvent) {
    // console.warn('onStartScrollDown()', $event);
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    // 한번은 바로 내려간다.
    this.container?.nativeElement.scrollTo({
      top: this.container.nativeElement.scrollTop + WORD_SCROLL_SPEED * 2,
      behavior: 'auto',
    });

    this.scrollDownInterval = setInterval(() => {
      this.container?.nativeElement.scrollTo({
        top: this.container.nativeElement.scrollTop + WORD_SCROLL_SPEED,
        behavior: 'auto',
      });
    }, LIST_SCROLL_INTERVAL);
  }

  onEndScrollDown($event: MouseEvent|TouchEvent) {
    // console.warn('onEndScrollDown()', $event);
    clearInterval(this.scrollDownInterval);
    this.scrollDownInterval = null;
  }

  /****************************************************************************
   * 브라우저 크기 변경 처리.
   ****************************************************************************/


  // 'resize' 이벤트를 감지하고 이벤트 발생 시 updateHeight 메소드를 호출합니다.
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.updateExtHeight();
  }

  private updateExtHeight(): void {
    if (this.extContainer && this.extHeader) {
      const containerHeight = this.extContainer.nativeElement.offsetHeight;
      const headerHeight = this.extHeader.offsetHeight;
      this.bodyHeight = containerHeight - headerHeight - EXT_HEIGHT_CAL;

      if (this.extBody) {
        this.extBody.nativeElement.style.height = this.bodyHeight + 'px';
      }

      if (this.extBody2) {
        const bodyHeight2 = Math.max(
          BODY_HEIGHT_VALUE - SIDE_HEIGHT_DELTA,
          this.bodyHeight + BODY_HEIGHT_CAL);
        this.extBody2.nativeElement.style.setProperty('--side-height', bodyHeight2 + 'px');
      }
    }
  }

}
